import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

const steps = [
  ['We read the context', 'A real person reviews the message and checks whether the project fits what we do.'],
  ['We talk through it', 'We clarify the goal, current setup, timing, decision-makers, and the smallest useful first release.'],
  ['You get a clear next step', 'That may be a proposal, a paid discovery, or an honest recommendation to take a different route.'],
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  service: 'not-sure',
  budget: '',
  message: '',
  website: '',
};

const Contact = () => {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState('idle');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formData.website) {
      setStatus('success');
      return;
    }

    setStatus('sending');
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          budget: formData.budget,
          message: formData.message,
        }),
      });
      const result = await response.json().catch(() => null);

      if (response.ok && result?.success) {
        setStatus('success');
        setFormData(initialForm);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const fieldClass = 'mt-2 w-full rounded-xl border border-[#CBD8DE] bg-[#F8FAFB] px-4 py-3.5 text-sm text-[#102A3A] transition placeholder:text-[#94A3AA] hover:border-[#A9BEC7] focus:border-[#176B8A] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#176B8A]/10';
  const labelClass = 'text-xs font-bold uppercase tracking-[0.12em] text-[#52636E]';

  return (
    <>
      <Helmet>
        <title>Start a Project | Contact Nexgeius Toronto</title>
        <meta name="description" content="Tell Nexgeius about your website, custom software, or AI automation project and receive a clear next step from our Toronto team." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.nexgeius.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Start a Project | Nexgeius" />
        <meta property="og:description" content="Bring the rough idea, current website, or manual process. We will help identify the clearest next step." />
        <meta property="og:url" content="https://www.nexgeius.com/contact" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Nexgeius',
          url: 'https://www.nexgeius.com/contact',
          mainEntity: {
            '@type': 'ProfessionalService',
            name: 'Nexgeius',
            email: 'nexgeius@gmail.com',
            address: { '@type': 'PostalAddress', addressLocality: 'Toronto', addressRegion: 'ON', addressCountry: 'CA' },
          },
        })}</script>
      </Helmet>

      <main>
        <section className="page-grid bg-[#F6F8FA] px-6 pb-16 pt-32 lg:px-8 lg:pb-20">
          <div className="relative z-10 mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1.1fr_.9fr] lg:items-end">
            <div>
              <p className="section-kicker">Start a project</p>
              <h1 className="mt-5 max-w-4xl text-[clamp(2.8rem,6vw,5.8rem)] font-semibold leading-[0.96] tracking-[-0.055em] text-[#102A3A]">Bring us the problem. It does not need to be polished.</h1>
            </div>
            <div className="max-w-xl lg:justify-self-end">
              <p className="text-lg leading-8 text-[#52636E]">Tell us what is happening now, what you want to change, and when it matters. We usually reply within one business day.</p>
              <a href="mailto:nexgeius@gmail.com" className="mt-7 inline-block text-lg font-semibold text-[#176B8A] hover:text-[#10566F]">nexgeius@gmail.com</a>
            </div>
          </div>
        </section>

        <section className="bg-[#F6F8FA] px-6 pb-24 lg:px-8 lg:pb-28">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[.7fr_1.3fr]">
            <aside className="rounded-[1.75rem] bg-[#102A3A] p-7 text-white md:p-9">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#92C5D8]">What happens next</p>
              <ol className="mt-9 space-y-8">
                {steps.map(([title, copy], index) => (
                  <li key={title} className="grid grid-cols-[38px_1fr] gap-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-xs font-semibold text-[#92C5D8]">0{index + 1}</span>
                    <div>
                      <h2 className="font-semibold">{title}</h2>
                      <p className="mt-2 text-sm leading-6 text-[#B9CAD2]">{copy}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-10 border-t border-white/15 pt-7">
                <p className="text-sm font-semibold">Prefer email?</p>
                <p className="mt-2 text-sm leading-6 text-[#B9CAD2]">Send the rough details to our inbox and we will take it from there.</p>
                <a href="mailto:nexgeius@gmail.com" className="mt-5 inline-flex rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#102A3A]">Email Nexgeius</a>
              </div>
            </aside>

            <div className="rounded-[1.75rem] border border-[#D5E0E5] bg-white p-7 shadow-[0_20px_60px_rgba(16,42,58,0.07)] md:p-10">
              {status === 'success' ? (
                <div className="flex min-h-[540px] flex-col items-center justify-center text-center" role="status">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E4F0F4] text-2xl text-[#176B8A]" aria-hidden="true">&#10003;</span>
                  <h2 className="mt-6 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Your message is on its way.</h2>
                  <p className="mt-3 max-w-md text-sm leading-6 text-[#63747E]">We will review the details and reply as soon as we can, usually within one business day.</p>
                  <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-sm font-semibold text-[#176B8A]">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-8">
                    <p className="section-kicker">Project enquiry</p>
                    <h2 className="mt-3 text-3xl font-semibold tracking-[-0.035em] text-[#102A3A]">Tell us enough to be useful.</h2>
                  </div>

                  <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input id="website" name="website" value={formData.website} onChange={handleChange} tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className={labelClass}>Name</label>
                      <input id="name" name="name" value={formData.name} onChange={handleChange} required autoComplete="name" placeholder="Your name" className={fieldClass} />
                    </div>
                    <div>
                      <label htmlFor="email" className={labelClass}>Email</label>
                      <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" placeholder="you@company.com" className={fieldClass} />
                    </div>
                    <div>
                      <label htmlFor="phone" className={labelClass}>Phone <span className="font-normal normal-case tracking-normal text-[#8A9AA3]">(optional)</span></label>
                      <input id="phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} autoComplete="tel" placeholder="(416) 555-0123" className={fieldClass} />
                    </div>
                    <div>
                      <label htmlFor="service" className={labelClass}>What do you need?</label>
                      <select id="service" name="service" value={formData.service} onChange={handleChange} className={fieldClass}>
                        <option value="not-sure">Not sure yet</option>
                        <option value="website-development">Website development</option>
                        <option value="custom-software">Custom software</option>
                        <option value="ai-automation">AI automation</option>
                        <option value="maintenance-support">Maintenance and support</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="budget" className={labelClass}>Working budget <span className="font-normal normal-case tracking-normal text-[#8A9AA3]">(optional)</span></label>
                    <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className={fieldClass}>
                      <option value="">Select a range</option>
                      <option value="under-2500">Under CA$2,500</option>
                      <option value="2500-5000">CA$2,500-5,000</option>
                      <option value="5000-10000">CA$5,000-10,000</option>
                      <option value="10000-25000">CA$10,000-25,000</option>
                      <option value="25000-plus">CA$25,000+</option>
                    </select>
                  </div>

                  <div className="mt-5">
                    <label htmlFor="message" className={labelClass}>What is happening now?</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows="6" placeholder="What are you trying to build or fix? Who is it for? Is there a deadline?" className={`${fieldClass} resize-y`} />
                  </div>

                  <div aria-live="polite" className="mt-4 min-h-6">
                    {status === 'error' && <p className="text-sm text-[#A33E36]">The form did not send. Please try again or email nexgeius@gmail.com directly.</p>}
                  </div>

                  <button type="submit" disabled={status === 'sending'} className="button-primary mt-3 inline-flex w-full items-center justify-center gap-2 disabled:cursor-wait disabled:opacity-60 sm:w-auto">
                    {status === 'sending' ? 'Sending...' : 'Send project enquiry'} {status !== 'sending' && <span aria-hidden="true">&rarr;</span>}
                  </button>
                  <p className="mt-4 text-xs leading-5 text-[#82939C]">Submitting this form sends the details to Nexgeius so we can respond to your enquiry. We do not ask for passwords or sensitive business data here.</p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact;
